<!--
  InstructorInvitesCard — 강사 등록 대기(동의 코드 요청) 목록
  · GET /voucher/org/{org}/instructors/invites?status=PENDING — 이메일·가린 이름·만료·발송/잘못 입력 횟수·요청자
  · 행마다 코드 입력(confirm)·재발송(POST invites {email})·취소(cancel)
-->
<template>
  <a-card title="등록 대기" :sub="`강사 동의 코드를 기다리는 요청 ${invites.length}건 · 강사가 전달한 코드를 입력하면 등록됩니다`">
    <template #action>
      <a-btn size="sm" kind="ghost" @click="load">새로고침</a-btn>
    </template>
    <div v-if="loading" class="p-loader"><div class="p-spinner" /></div>
    <a-empty v-else-if="!invites.length" title="대기 중인 등록 요청이 없습니다" />
    <a-table v-else :columns="cols" :rows="invites" row-key="invite_idx" dense>
      <template #cell-email="{ row }">
        <div class="p-cell-title">{{ row.name || '-' }}</div>
        <div class="p-cell-sub p-mono">{{ row.email }}</div>
      </template>
      <template #cell-expires_at="{ row }">
        <span class="p-mono">{{ fmtDateTime(row.expires_at) }}</span>
        <div v-if="row.last_sent_at" class="p-cell-sub">마지막 발송 {{ fmtDateTime(row.last_sent_at) }}</div>
      </template>
      <template #cell-counts="{ row }">
        <span class="p-mono">발송 {{ row.sent_count == null ? 0 : row.sent_count }}<template v-if="row.max_sends">/{{ row.max_sends }}</template></span>
        <div class="p-cell-sub p-mono">잘못 입력 {{ row.attempts == null ? 0 : row.attempts }}<template v-if="row.max_attempts">/{{ row.max_attempts }}</template></div>
      </template>
      <template #cell-requested_by_name="{ row }">
        <span class="p-cell-sub">{{ row.requested_by_name || row.requested_by_emp_name || '-' }}</span>
      </template>
      <template #cell-actions="{ row }">
        <div class="p-invite-actions">
          <input
            v-model.trim="codes[row.invite_idx]"
            type="text"
            inputmode="numeric"
            maxlength="6"
            class="p-input p-input--mono p-invite-code"
            placeholder="코드"
            @keyup.enter="confirm(row)"
          />
          <a-btn size="sm" kind="primary" :disabled="!isCode(codes[row.invite_idx]) || busy === row.invite_idx" @click="confirm(row)">확인</a-btn>
          <a-btn size="sm" kind="ghost" :disabled="busy === row.invite_idx" @click="resend(row)">재발송</a-btn>
          <a-btn size="sm" kind="danger" :disabled="busy === row.invite_idx" @click="cancel(row)">취소</a-btn>
        </div>
        <div v-if="errors[row.invite_idx]" class="p-cell-sub p-err">{{ errors[row.invite_idx] }}</div>
      </template>
    </a-table>
  </a-card>
</template>

<script>
import ACard from '@/components/Pineapple/ACard.vue';
import ATable from '@/components/Pineapple/ATable.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';
import AEmpty from '@/components/Pineapple/AEmpty.vue';
import { fmtDateTime, voucherError, voucherErrorText } from '../orgUtils';

export default {
  name: 'InstructorInvitesCard',
  components: { ACard, ATable, ABtn, AEmpty },
  props: {
    orgIdx: { type: [Number, String], required: true },
  },
  emits: ['confirmed'],
  data() {
    return {
      invites: [],
      loading: false,
      busy: null,
      codes: {},
      errors: {},
      cols: [
        { key: 'email',             label: '강사(동의 전에는 이름이 가려짐)' },
        { key: 'expires_at',        label: '만료',          width: '220px' },
        { key: 'counts',            label: '발송 / 잘못 입력', width: '130px' },
        { key: 'requested_by_name', label: '요청자',        width: '110px' },
        { key: 'actions',           label: '',              width: '330px', align: 'right' },
      ],
    };
  },
  watch: {
    orgIdx() { this.load(); },
  },
  mounted() {
    this.load();
  },
  methods: {
    fmtDateTime,
    isCode(v) { return typeof v === 'string' && v.length === 6; },
    async load() {
      if (!this.orgIdx) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          method: 'get',
          url: `/voucher/org/${this.orgIdx}/instructors/invites`,
          params: { status: 'PENDING' },
          loadingBar: false,
        });
        this.invites = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        this.invites = [];
      } finally {
        this.loading = false;
      }
    },
    setError(row, msg) { this.errors = { ...this.errors, [row.invite_idx]: msg }; },
    async confirm(row) {
      const code = this.codes[row.invite_idx];
      if (!this.isCode(code)) return;
      this.busy = row.invite_idx;
      this.setError(row, '');
      try {
        const res = await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/instructors/invites/${row.invite_idx}/confirm`,
          data: { code },
          loadingBar: true,
        });
        const m = res.data || {};
        this.$notifyVue({ type: 'success', title: `"${m.name || row.email}" 강사를 기관에 등록했습니다.` });
        this.$emit('confirmed', m);
        this.load();
      } catch (e) {
        const err = voucherError(e);
        if (err.code === 'ORG_INVITE_CODE_INVALID') {
          const left = err.detail && err.detail.attempts_left != null ? ` (남은 ${err.detail.attempts_left}회)` : '';
          this.setError(row, `코드가 맞지 않습니다${left}.`);
        } else {
          this.setError(row, voucherErrorText(e, '코드를 확인하지 못했습니다.'));
          if (['ORG_INVITE_NOT_PENDING', 'ORG_INVITE_NOT_FOUND', 'ORG_INVITE_EXPIRED'].includes(err.code)) this.load();
        }
      } finally {
        this.busy = null;
      }
    },
    async resend(row) {
      this.busy = row.invite_idx;
      this.setError(row, '');
      try {
        await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/instructors/invites`,
          data: { email: row.email },
          loadingBar: true,
        });
        this.$notifyVue({ type: 'success', title: `"${row.email}" 로 동의 코드를 다시 보냈습니다.` });
        this.load();
      } catch (e) {
        const err = voucherError(e);
        let msg = voucherErrorText(e, '다시 보내지 못했습니다.');
        if (err.code === 'ORG_INVITE_RATE_LIMITED' && err.detail && err.detail.retry_after) msg += ` ${err.detail.retry_after}초 뒤 가능.`;
        this.setError(row, msg);
      } finally {
        this.busy = null;
      }
    },
    async cancel(row) {
      const ok = await this.$swal({
        title: '등록 요청 취소',
        text: `"${row.email}" 에 보낸 동의 코드 요청을 취소하시겠습니까?`,
        icon: 'warning',
        showConfirmButton: true, showCancelButton: true,
        confirmButtonText: '취소하기', cancelButtonText: '닫기',
        confirmButtonColor: '#C44A3A',
      });
      if (!ok.isConfirmed) return;
      this.busy = row.invite_idx;
      try {
        await this.$axios({
          method: 'post',
          url: `/voucher/org/${this.orgIdx}/instructors/invites/${row.invite_idx}/cancel`,
          loadingBar: true,
        });
        this.$notifyVue({ type: 'success', title: '등록 요청을 취소했습니다.' });
        this.load();
      } catch (e) {
        this.setError(row, voucherErrorText(e, '요청을 취소하지 못했습니다.'));
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../_org.scss';

.p-invite-actions { display: inline-flex; gap: 6px; align-items: center; }
.p-invite-code { width: 90px; text-align: center; letter-spacing: 0.15em; padding-top: 6px; padding-bottom: 6px; }
.p-err { color: $p-err; margin-top: 4px; text-align: right; }
</style>
